// Scheduling arithmetic of the timeline preview (useTimelinePreview) —
// pure, so it is testable without Web Audio. The hook keeps the side
// effects (createBufferSource, rAF, duck/unduck, video sync).

/** When/where a voice clip starts relative to the shared context-time
 *  `anchor` (all sources share one anchor so their relative alignment
 *  survives JS scheduling jitter). `null` = clip already over at
 *  `startFrom`, don't schedule. Clip under the playhead → starts now,
 *  `offsetIntoBuffer` into the audio; clip later → delayed start from 0. */
export function voiceStartParams(
  clipOffset: number,
  bufferDuration: number,
  startFrom: number,
  anchor: number,
): { when: number; offsetIntoBuffer: number } | null {
  if (clipOffset + bufferDuration <= startFrom) return null;
  if (clipOffset <= startFrom) return { when: anchor, offsetIntoBuffer: startFrom - clipOffset };
  return { when: anchor + (clipOffset - startFrom), offsetIntoBuffer: 0 };
}

/** Playhead position: `raw` may be negative during the output-latency
 *  pre-roll (used for active-block detection so a block at 0 doesn't duck
 *  before audio emits), `elapsed` is clamped at 0 for display. */
export function previewElapsed(
  baseElapsed: number,
  baseContextTime: number,
  contextNow: number,
  latency: number,
): { raw: number; elapsed: number } {
  const raw = baseElapsed + (contextNow - baseContextTime) - latency;
  return { raw, elapsed: Math.max(0, raw) };
}

export interface PreviewEntryLike {
  blockIdx: number;
  offset: number;
  duration?: number;
  buffer?: { duration: number } | null;
}

/** First schedule entry covering `raw` (buffer duration wins over the
 *  stored duration), -1 when none. Half-open: [offset, offset + dur). */
export function activeBlockAt(schedule: ReadonlyArray<PreviewEntryLike>, raw: number): number {
  for (const entry of schedule) {
    const dur = entry.buffer?.duration || entry.duration || 0;
    if (raw >= entry.offset && raw < entry.offset + dur) return entry.blockIdx;
  }
  return -1;
}

/** Ducking follows voice presence, not block identity: only the edges
 *  silence → voice (duck) and voice → silence (unduck) switch it. */
export function duckTransition(prevActive: number, nextActive: number): 'duck' | 'unduck' | null {
  if (nextActive === prevActive) return null;
  if (nextActive !== -1 && prevActive === -1) return 'duck';
  if (nextActive === -1 && prevActive !== -1) return 'unduck';
  return null;
}
